import React from "react";
import styled, { css, keyframes } from "styled-components";
import { tokens } from "../../theme/tokens";

// Branded Button. Presentation-only — pass onClick/type/disabled as usual.
//
//   <Button variant="primary" size="md" loading leadingIcon={<Icon/>}>Save</Button>
//
// variants: primary | secondary | outline | ghost | danger
// sizes:    sm | md | lg

const { colors, radii, shadows, spacing, typography, motion } = tokens;

const sizes = {
    sm: css`
        height: 34px;
        padding: 0 14px;
        font-size: ${typography.size.sm};
    `,
    md: css`
        height: 42px;
        padding: 0 20px;
        font-size: ${typography.size.base};
    `,
    lg: css`
        height: 50px;
        padding: 0 28px;
        font-size: ${typography.size.md};
    `,
};

const variants = {
    primary: css`
        background: linear-gradient(135deg, ${colors.vermilion} 0%, ${colors.crimson} 55%, ${colors.maroon} 100%);
        color: ${colors.onDark};
        box-shadow: ${shadows.sm};
        &:hover:not(:disabled) { box-shadow: ${shadows.md}; transform: translateY(-1px); }
        &:active:not(:disabled) { transform: translateY(0); box-shadow: ${shadows.xs}; }
    `,
    secondary: css`
        background: ${colors.ink};
        color: ${colors.onDark};
        &:hover:not(:disabled) { background: #3d1616; transform: translateY(-1px); }
    `,
    outline: css`
        background: ${colors.surface};
        color: ${colors.primary};
        box-shadow: inset 0 0 0 1.5px ${colors.borderStrong};
        &:hover:not(:disabled) { box-shadow: inset 0 0 0 1.5px ${colors.primary}; background: ${colors.rose}; }
    `,
    ghost: css`
        background: transparent;
        color: ${colors.heading};
        &:hover:not(:disabled) { background: ${colors.surfaceSunken}; }
    `,
    danger: css`
        background: ${colors.status.danger.fg};
        color: ${colors.onDark};
        &:hover:not(:disabled) { background: #8f0f18; transform: translateY(-1px); }
    `,
};

const spin = keyframes`to { transform: rotate(360deg); }`;

const Root = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${spacing[2]};
    border: 0;
    border-radius: ${radii.pill};
    font-family: ${typography.fonts.body};
    font-weight: ${typography.weight.bold};
    letter-spacing: 0.01em;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    text-decoration: none;
    transition: background ${motion.base} ${motion.ease},
        box-shadow ${motion.base} ${motion.ease},
        transform ${motion.fast} ${motion.ease},
        color ${motion.base} ${motion.ease};
    width: ${(p) => (p.$fullWidth ? "100%" : "auto")};

    ${(p) => sizes[p.$size] || sizes.md};
    ${(p) => variants[p.$variant] || variants.primary};

    &:focus-visible { outline: none; box-shadow: ${shadows.focus}; }
    &:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
`;

const Spin = styled.span`
    width: 1em;
    height: 1em;
    border-radius: 50%;
    border: 2px solid currentColor;
    border-right-color: transparent;
    animation: ${spin} 0.7s linear infinite;
    display: inline-block;
`;

const Button = React.forwardRef(function Button(
    {
        variant = "primary",
        size = "md",
        fullWidth = false,
        loading = false,
        leadingIcon,
        trailingIcon,
        children,
        disabled,
        type = "button",
        ...rest
    },
    ref
) {
    return (
        <Root
            ref={ref}
            type={type}
            $variant={variant}
            $size={size}
            $fullWidth={fullWidth}
            disabled={disabled || loading}
            {...rest}
        >
            {loading ? <Spin aria-hidden /> : leadingIcon}
            {children}
            {!loading && trailingIcon}
        </Root>
    );
});

export default Button;
